
import { Component } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import { AccountState } from '@/store/account';
import DowngradeRestrictedModal from '@/components/Modals/Account/DowngradeRestrictedModal.vue';
import SelectFreePlanModal from '@/components/Modals/Account/SelectFreePlanModal.vue';
import KeyValueItem from '@/Models/PrimitiveModels/KeyValueItem';
import { Currencies, CurrencySymbols } from '@/Models/Localization';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import { filterAndSortPlans } from '@/Services/Subscription/PlanViewService';
import EnterprisePlan from '@/Pages/PricingPage/EnterprisePlan.vue';
import PlanTile from '@/Pages/PricingPage/PlanTile.vue';
import EventService from '@/Services/EventService';
import { Plan, Subscription, SubscriptionState } from '@/classes/stripe';
import { Team, User } from '@/classes/auth';
import Alert from '@/components/Alert.vue';
import { SupportModalMixin } from '@/mixins/SupportModalMixin';
import store from '@/store';

@Component({
  components: {
    DowngradeRestrictedModal,
    SelectFreePlanModal,
    EnterprisePlan,
    PlanTile,
    Alert,
  },
  computed: {
    ...mapState<SubscriptionState>('subscription', {
      subscriptionPlans: (state: SubscriptionState) => state.plans,
      loadingPlans: (state: SubscriptionState) => state.showLoading.loadingPlans,
      loadingSubscription: (state: SubscriptionState) => state.showLoading.loadingSubscription,
      activeSubscription: (state: SubscriptionState) => state.activeSubscription,
    }),
    ...mapState<AccountState>('account', {
      account: (state: AccountState) => state.account,
      qrcodes: (state: AccountState) => state.account?.qrcodes || [],
    }),
    ...mapGetters('auth', {
      user: 'user',
      team: 'team',
    }),
    ...mapGetters('subscription', {
      isEnterprise: 'isEnterprise', // <- for runtime
    }),
  },
})
export default class PlanSelection extends SupportModalMixin {
  // state from vuex
  user!: User;
  team!: Team;
  loadingPlans!: boolean;
  loadingSubscription!: boolean;
  subscription: Subscription = new Subscription();
  subscriptionPlans!: Array<Plan>;
  activeSubscription!: Subscription;

  openDowngradeRestrictedModal = false;
  openSelectFreePlanModal = false;
  getInTouchLink = 'https://form.asana.com/?k=0FszAJp6r2L5vtEiSrj9Ag&d=1174264937396';

  account!: any;
  qrcodes!: any;

  // List of currencies to choose from to preview different currency exchange-rates later on
  currencies: Array<KeyValueItem> = [
    {
      key: Currencies.EUR,
      value: `${CurrencySymbols.EUR}EUR`,
    },
    {
      key: Currencies.USD,
      value: `$USD`,
    },
    {
      key: Currencies.GBP,
      value: `${CurrencySymbols.GBP}GBP`,
    },
  ];

  // Limits of this plan as a clean list, null values won't be shown
  faqList: Array<{ question: string; answer: string; freePlanAddition?: string }> = [];

  // Which currency exchange-rates shall we gonna display to the customer
  selectedCurrency = Currencies.EUR;

  // Used to display active subscription state inside plan selection
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  // Loading indicator
  isLoading = false;

  // getter from vuex
  isEnterprise!: boolean;

  mounted(): void {
    this.faqList = [
      {
        question: this.$t('fields.pricing_page.faq.question1').toString(),
        answer: this.$t('fields.pricing_page.faq.answer1').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question2').toString(),
        answer: this.$t('fields.pricing_page.faq.answer2').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question3').toString(),
        answer: this.$t('fields.pricing_page.faq.answer3').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question4').toString(),
        answer: this.$t('fields.pricing_page.faq.answer4').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question5').toString(),
        answer: this.$t('fields.pricing_page.faq.answer5').toString(),
        freePlanAddition: this.$t('fields.pricing_page.faq.free_plan_info').toString(),
      },
    ];
  }

  async created(): Promise<void> {
    this.isLoading = true;
    await this.stripeSubscriptionService.initSubscription();
    this.isLoading = false;

    if (this.$route.query.trial === 'expired') {
      this.showTrialExpiredBanner();
    }

    EventService.createEvent(EventService.VISITED_PRICING);
  }

  showTrialExpiredBanner(): void {
    store.dispatch('layout/setSnackbar', {
      active: true,
      type: 'error',
      icon: 'warning-system',
      text: this.$t('fields.pricing_page.trial-expired-title').toString(),
      isSnackbar: false,
      sticky: true,
      showDismissButton: false,
      textClass: 'trial-expired-title',
    });
  }

  get accountIsTrial(): boolean {
    return !!this.account?.trial;
  }

  get accountHasFreePlan(): boolean {
    return this.account?.type === 'free';
  }

  get returnPlans(): Array<Plan> {
    return filterAndSortPlans(this.subscriptionPlans);
  }

  /**
   * If the user can select a free plan, he will see the modal to proceed
   * If the user can't select a free plan, he will see a restriction modal
   */
  clickOnActivateFreePlan(): void {
    if (this.stripeSubscriptionService.canSelectFreePlan) {
      this.openSelectFreePlanModal = true;
    } else {
      this.openDowngradeRestrictedModal = true;
    }
  }

  /**
   * Called when a user clicks cancel in downgrade-restricted modal
   */
  onDowngradeRestrictedModalCancel(): void {
    this.openDowngradeRestrictedModal = false;
  }

  /**
   * Called when a user clicks cancel in free plan modal
   */
  onSelectFreePlanModalCancel(): void {
    this.openSelectFreePlanModal = false;
  }
}
